// Composable for Firebase Cloud Messaging push notifications.
//
// Flow:
//   initPushNotifications() — call on mount after auth:
//     • Registers the service worker (no user gesture needed)
//     • If permission already 'granted' → silently gets token + saves to backend
//     • If permission 'default' → asks through the browser's native permission UI
//     • If permission 'denied'  → skips silently
//
//   enablePushNotifications():
//     • Calls Notification.requestPermission() to show the native browser prompt
//     • On grant → gets token + saves to backend + hides prompt
//
//   listenForForegroundMessages() — call once after auth:
//     • Shows in-app toast, refreshes badge + list when a push arrives while tab is open
//
// usePushNotifications() returns:
//   { showPermissionPrompt, initPushNotifications, enablePushNotifications, listenForForegroundMessages }
// Side effects: registers a service worker, may trigger the native browser
// permission prompt, posts the FCM token to the backend, listens for
// foreground/background/visibilitychange events, and dispatches Vuex actions
// to refresh notification and message state.

import { ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { registerServiceWorker, getFCMToken, onForegroundMessage } from '@/services/firebase';
import { showNotification } from '@/plugins/notification';
/** @typedef {import('firebase/messaging').MessagePayload} MessagePayload */

// Backend contract per PUSH_NOTIFICATIONS.md (the Android app's documented,
// working contract): POST /api/notifications/update-token with
// { newToken, oldToken, platform }, empty newToken = unregister. The web
// client previously posted to an undocumented /api/fcm-token endpoint the
// backend doesn't implement, so tokens were silently never saved — that's
// why push never worked on web.
const UPDATE_TOKEN_URL = '/api/notifications/update-token';
const FCM_TOKEN_STORAGE_KEY = 'fcm_token';

/** POST a token add/replace (newToken set) or unregister (newToken empty) to the backend. */
async function postTokenUpdate(newToken, oldToken) {
  return fetch(UPDATE_TOKEN_URL, {
    method: 'POST',
    // Unregister-on-logout fires right before window.location.replace() —
    // keepalive lets it complete after the page starts unloading, same as
    // the /api/auth/logout call in the logOut action.
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ newToken, oldToken, platform: 'web' }),
  });
}

/**
 * Call on logout — mirrors the Android app's unregister-on-logout step so the
 * backend stops sending pushes to a device that's no longer signed in.
 * Clears the locally stored FCM token regardless of whether the request succeeds.
 */
export async function unregisterPushToken() {
  const oldToken = localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
  if (!oldToken) return;
  try {
    await postTokenUpdate('', oldToken);
  } catch (err) {
    console.error('[FCM] Unregister failed:', err);
  } finally {
    localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
  }
}

/**
 * Sets up FCM push notifications for the current session: service worker
 * registration, permission handling, token persistence, and foreground/
 * background message handling that refreshes notification and message state.
 */
export function usePushNotifications() {
  const store = useStore();
  const route = useRoute();
  const router = useRouter();

  // Kept for callers that want to build an explicit user-action prompt later.
  // The app currently uses only the browser's native permission UI.
  const showPermissionPrompt = ref(false);

  let _swRegistration = null;
  const refreshDelays = [0, 500, 1500];

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  /** Normalizes the `data` payload across the different shapes FCM/SW messages arrive in. */
  function getPayloadData(payload) {
    return payload?.payload?.data || payload?.data || {};
  }

  /** Extracts a conversation id from a push payload, tolerating multiple key-naming conventions. */
  function getMessageConversationId(payload) {
    const data = getPayloadData(payload);
    return data.conversationId || data.conversation_id || data.message_channel_id || data.messageChannelId || null;
  }

  /** Broadcasts a global DOM event so other parts of the app (outside this composable) can react to an incoming push. */
  function notifyNotificationReceived(source, payload = null) {
    window.dispatchEvent(new CustomEvent('notifications:received', {
      detail: { source, payload },
    }));
  }

  /**
   * Fetches the current FCM token and, if it differs from what's already
   * saved, POSTs it to the backend and updates local storage.
   * @param {ServiceWorkerRegistration} swRegistration
   */
  async function saveToken(swRegistration) {
    const token = await getFCMToken(swRegistration);
    if (!token) return;

    // const res = await fetch('/api/fcm-token', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({ token }),
    // });
    const newToken = await getFCMToken(swRegistration);
    if (!newToken) return;

    const oldToken = localStorage.getItem(FCM_TOKEN_STORAGE_KEY) || '';
    const res = await postTokenUpdate(newToken, oldToken);

    if (res.ok) {
      localStorage.setItem(FCM_TOKEN_STORAGE_KEY, newToken);
      console.log('[FCM] Token saved to backend');
    } else {
      console.error('[FCM] Backend rejected token — status:', res.status);
    }
  }

  /**
   * Call on mount after auth. Registers the service worker and silently saves
   * the token if permission was already granted in a previous session; if
   * permission is still undecided, falls through to enablePushNotifications()
   * to show the native prompt. Does nothing if permission was denied.
   */
  async function initPushNotifications() {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;

    _swRegistration = await registerServiceWorker();
    if (!_swRegistration) return;

    const permission = Notification.permission;
    console.log('[FCM] Current permission:', permission);

    if (permission === 'granted') {
      await saveToken(_swRegistration);
    } else if (permission === 'default') {
      await enablePushNotifications();
    }
    // 'denied' → do nothing.
  }

  /** Shows the native browser permission prompt and, on grant, saves the FCM token. */
  async function enablePushNotifications() {
    const permission = await Notification.requestPermission();
    console.log('[FCM] Permission after prompt:', permission);

    showPermissionPrompt.value = false;

    if (permission === 'granted' && _swRegistration) {
      await saveToken(_swRegistration);
    }
  }

  /**
   * Refreshes the unread messages count and conversation list, and if the
   * user currently has the pushed (or no specific) conversation open on the
   * /messages route, re-selects it to pull in the new message.
   */
  async function refreshMessagesState(payload = null) {
    const activeConversationId = store.getters.getActiveConversationId;
    const pushedConversationId = getMessageConversationId(payload);

    await Promise.all([
      store.dispatch('fetchUnreadMessagesCount'),
      store.dispatch('fetchConversations', { search: store.getters.getConversationsSearch || '' }),
    ]);

    if (
      route.path === '/messages' &&
      activeConversationId &&
      (!pushedConversationId || pushedConversationId === activeConversationId)
    ) {
      await store.dispatch('selectConversation', activeConversationId);
    }
  }

  /**
   * Refreshes unread notification count, message state, and (if on the
   * relevant route) the visible lists. When `retry` is set, repeats the
   * refresh at increasing delays (see refreshDelays) to smooth over backend
   * read-after-write lag right after a push arrives.
   */
  async function refreshNotificationState(payload = null, { retry = false } = {}) {
    const delays = retry ? refreshDelays : [0];

    for (const delay of delays) {
      if (delay) await sleep(delay);

      // Any push can be a new-message notification (no `type` field to
      // branch on in the payload), so the Leftbar's messages badge gets
      // refreshed alongside the notifications one here too, same
      // retry/backoff — mirrors how initMessages/fetchUnreadMessagesCount
      // are already fetched together with fetchUnreadNotificationsCount at
      // boot in App.vue.
      await Promise.all([
        store.dispatch('fetchUnreadNotificationsCount'),
        refreshMessagesState(payload),
      ]);
      if (route.path === '/notifications') {
        await Promise.all([
          store.dispatch('fetchNotifications', { read: false, limit: 10, offset: 0 }),
          store.dispatch('fetchNotifications', { read: true, limit: 10, offset: 0 }),
        ]);
      }
    }
  }

  /** Fires the global "received" event and refreshes state (with retry) for a newly arrived push. */
  async function handleNewNotification(source, payload) {
    notifyNotificationReceived(source, payload);
    await refreshNotificationState(payload, { retry: true });
  }

  /** Refreshes notification/message state when the tab regains visibility, without the retry backoff. */
  async function handleVisibilityRefresh() {
    await Promise.all([
      store.dispatch('fetchUnreadNotificationsCount'),
      refreshMessagesState(),
    ]);
    if (route.path === '/notifications') {
      await Promise.all([
        store.dispatch('fetchNotifications', { read: false, limit: 10, offset: 0 }),
        store.dispatch('fetchNotifications', { read: true, limit: 10, offset: 0 }),
      ]);
    }
  }

  /**
   * Subscribes to FCM messages via three channels:
   * 1. onMessage        — tab in foreground (Firebase delivers directly)
   * 2. SW postMessage   — tab in background (SW receives push, relays to main thread)
   * 3. visibilitychange — safety net: refresh count whenever user focuses the tab
   * Call once after auth. Registers listeners for the lifetime of the page.
   */
  function listenForForegroundMessages() {
    // Channel 1: tab focused
    onForegroundMessage(async (/** @type {MessagePayload} */ payload) => {
      const title = payload.notification?.title || 'New notification';
      const body  = payload.notification?.body  || '';
      const conversationId = getMessageConversationId(payload);

      if (conversationId) {
        // Own toast group ('message-nav') so only this toast renders with the
        // clickable template wired up in default.vue — other toasts are untouched.
        showNotification('info', title, body, 5000, true, 'message-nav', null, null, {
          onClick: () => router.push({ path: '/messages', query: { conversationId } }),
        });
      } else {
        showNotification('info', title, body, 5000);
      }

      await handleNewNotification('foreground', payload);
    });

    // Channel 2: tab in background — service worker relays via postMessage
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', async (event) => {
        if (event.data?.type === 'FCM_NEW_NOTIFICATION') {
          await handleNewNotification('service-worker', event.data?.payload || event.data);
        }
      });
    }

    // Channel 3: visibilitychange fallback — catches any missed pushes when
    // the user switches back to the tab (e.g. after reading the OS notification)
    document.addEventListener('visibilitychange', async () => {
      if (document.visibilityState === 'visible') {
        await handleVisibilityRefresh();
      }
    });
  }

  return {
    showPermissionPrompt,
    initPushNotifications,
    enablePushNotifications,
    listenForForegroundMessages,
  };
}
